import React from "react";
import { MondayApiQueryInstance } from "@services/monday/monday-service-ts";
import ButtonComponent from "@components/Button";
import { IoSync } from "react-icons/io5";
import { LearnMore } from "@vibe/icons";
import { EXTERNAL_LINKS } from "@data/externalLinks";
import useMondayContext from "@hooks/context/useMondayContext";
import useFireNudgeTrigger from "../../hooks/api/useFireNudgeTrigger";

const SYNC_WIZARD_PATH = "/sync-wizard";
const SYNC_WIZARD_HEIGHT = "650px";
const SYNC_WIZARD_WIDTH = "580px";

type SyncedItemLocation = {
  item_id?: string;
  board_id?: string;
  itemid?: string;
  boardid?: string;
};

type SyncedItem = {
  items: SyncedItemLocation[];
};

type ActionHeaderProps = {
  syncedItems?: SyncedItem[];
  currentBoard?: number | null;
  excludedItemIds?: string[];
  isLoading?: boolean;
};

const getExcludedItemIds = (syncedItems: SyncedItem[], currentBoard?: number | null): string[] => {
  if (!currentBoard) return [];

  return Array.from(
    new Set(
      syncedItems.flatMap((syncedItem) =>
        syncedItem.items
          .filter((item) => String(item.board_id ?? item.boardid) === String(currentBoard))
          .map((item) => String(item.item_id ?? item.itemid))
          .filter((itemId) => itemId !== "undefined"),
      ),
    ),
  );
};

export function ActionHeader({
  syncedItems = [],
  currentBoard = null,
  excludedItemIds = [],
  isLoading = false,
}: ActionHeaderProps) {
  const { appFeatureType } = useMondayContext();
  const fireNudge = useFireNudgeTrigger();

  const handleShareAnotherItem = () => {
    if (isLoading) return;

    // A3: "share item" clicked in a board view. Best-effort, never blocks the
    // modal — deep-link to the last created item on the board.
    if (appFeatureType === "AppFeatureBoardView" && currentBoard) {
      MondayApiQueryInstance.getLastCreatedItemId(currentBoard)
        .then((targetItemId) => {
          if (targetItemId) {
            fireNudge({
              source: "share_opened_board",
              itemid: targetItemId,
              boardid: currentBoard,
              viewType: "board_view",
            });
          }
        })
        .catch(() => {});
    }

    const itemIdsToExclude =
      excludedItemIds.length > 0 ? excludedItemIds : getExcludedItemIds(syncedItems, currentBoard);

    void MondayApiQueryInstance.openAppFeatureModal(
      SYNC_WIZARD_PATH,
      itemIdsToExclude.length > 0 ? { excludedItemIds: itemIdsToExclude } : null,
      SYNC_WIZARD_HEIGHT,
      SYNC_WIZARD_WIDTH,
    );
  };

  return (
    <div className="flex items-center justify-between  border border-b-1 border-t-0 border-x-0 border-solid border-[#e6e9ef] py-3 px-12 bg-[#fafafa] flex-shrink-0">
      <div className="max-w-[1200px] flex justify-between w-full mx-auto">
        <div className="flex gap-3 items-center">
          <ButtonComponent
            text="Share another item"
            onClick={handleShareAnotherItem}
            kind="primary"
            size="small"
            leftIcon={IoSync}
            disabled={isLoading}
          />
        </div>
        <div className="flex gap-[6px] items-center">
          <LearnMore size={14} color="#0073EA" />
          <a
            className="text-sm text-[#0073EA] cursor-pointer hover:underline no-underline"
            href={EXTERNAL_LINKS.HOW_TO_INSTALL}
            target="_blank"
            rel="noopener noreferrer"
          >
            How does this work?
          </a>
        </div>
      </div>
    </div>
  );
}
